import { useEffect, useState } from 'react'
import { subscribeToAnonymousAudit } from '@/api/ws'
import { anonymousAuditsApi } from '@/api/client'
import type { AnonymousAuditResult, AuditPhase, AuditProgressMessage } from '@/api/client'

interface AnonymousAuditProgressState {
  phase: AuditPhase | null
  progress: number
  message: string
  result: AnonymousAuditResult | null
}

/**
 * Mirror of {@link useAuditProgress} for the unauthenticated demo flow.
 *
 * <p>Reload recovery: if the user lands on /audit-demo/:id after the audit
 * already finished, the WS broadcast is long gone. We probe the cache via
 * GET /api/audit/anonymous/:id on mount — if cached, jump straight to results;
 * if not, sit on the loading state waiting for a WS frame that may never come
 * (TTL evict). Refreshing an anonymous audit is explicitly unsupported per the
 * "you only see it once" product decision; this just keeps the page from
 * silently breaking when the result is still cached.</p>
 */
export function useAnonymousAuditProgress(auditId: string | undefined) {
  const [state, setState] = useState<AnonymousAuditProgressState>({
    phase: null,
    progress: 0,
    message: '',
    result: null,
  })

  useEffect(() => {
    if (!auditId) return
    let cancelled = false

    const fetchResult = async (message: string) => {
      try {
        const result = await anonymousAuditsApi.getResult(auditId)
        if (cancelled) return
        if (result) {
          setState({ phase: 'complete', progress: 100, message, result })
        } else {
          setState({ phase: 'error', progress: 0, message: 'Report expired or unavailable', result: null })
        }
      } catch {
        if (cancelled) return
        setState({ phase: 'error', progress: 0, message: 'Could not load report', result: null })
      }
    }

    // Reload recovery — probe cache. Silent on miss; the WS may still deliver.
    anonymousAuditsApi.getResult(auditId).then(result => {
      if (cancelled || !result) return
      setState({ phase: 'complete', progress: 100, message: 'Audit complete', result })
    }).catch(() => { /* wait for WS */ })

    const unsub = subscribeToAnonymousAudit(auditId, (msg: AuditProgressMessage) => {
      if (cancelled) return
      if (msg.phase === 'complete') {
        setState({ phase: 'scoring', progress: 100, message: 'Loading report…', result: null })
        fetchResult(msg.message || 'Audit complete')
      } else {
        setState({
          phase: msg.phase,
          progress: msg.progress,
          message: msg.message,
          result: null,
        })
      }
    })

    return () => {
      cancelled = true
      unsub()
    }
  }, [auditId])

  return state
}
